<template>
    <!-- the camera, reading QR codes one after the other until it is closed -->
    <v-dialog :value="value" max-width="520" @input="$emit('input', $event)">
        <v-card class="qr-scanner">
            <v-card-title class="qr-scanner__title">
                <v-icon left>mdi-qrcode-scan</v-icon>
                {{ title }}
                <v-spacer />
                <v-btn icon small @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <div class="qr-scanner__view">
                <video ref="video" playsinline muted />
                <div v-if="!error" class="qr-scanner__frame" />
                <div v-if="error" class="qr-scanner__error">{{ error }}</div>
            </div>
            <v-card-text class="qr-scanner__text">
                <div v-if="status" class="qr-scanner__status" :class="`qr-scanner__status--${status.type}`">{{ status.text }}</div>
                <div class="qr-scanner__hint">{{ hint }}</div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import jsQR from 'jsqr'

    // how often a frame is read, and how long the same code is not read again
    const SCAN_MS = 250
    const REPEAT_MS = 3000

    export default {
        name: 'QrScanner',
        props: {
            // the dialog is open
            value: {
                type: Boolean,
                default: false
            },
            title: {
                type: String,
                default: 'Scan QR'
            },
            hint: {
                type: String,
                default: ''
            },
            // {text, type: success | info | error}: what came of the last code
            status: {
                type: Object,
                default: null
            }
        },
        data() {
            return {
                error: null,
                stream: null,
                timer: null,
                detector: null,
                canvas: null,
                last: { text: null, at: 0 }
            }
        },
        watch: {
            value: {
                immediate: true,
                handler(open) {
                    if (open) this.$nextTick(this.start)
                    else this.stop()
                }
            }
        },
        beforeDestroy() {
            this.stop()
        },
        methods: {
            async start() {
                this.error = null
                this.last = { text: null, at: 0 }
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    this.error = 'The camera is not available here (it needs https).'
                    return
                }
                try {
                    this.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
                } catch (error) {
                    this.error = error && error.name === 'NotAllowedError'
                        ? 'Allow the use of the camera to scan.'
                        : 'The camera could not be opened.'
                    return
                }
                // closed while the camera was asked for
                if (!this.value) {
                    this.stop()
                    return
                }
                const video = this.$refs.video
                video.srcObject = this.stream
                await video.play().catch(() => {})
                // the reader of the browser where there is one (Chrome on Android, Safari 17+), else jsQR
                this.detector = null
                if ('BarcodeDetector' in window) {
                    try {
                        const formats = await window.BarcodeDetector.getSupportedFormats()
                        if (formats.includes('qr_code')) this.detector = new window.BarcodeDetector({ formats: ['qr_code'] })
                    } catch (error) {
                        this.detector = null
                    }
                }
                this.timer = setInterval(this.read, SCAN_MS)
            },
            stop() {
                clearInterval(this.timer)
                this.timer = null
                if (this.stream) {
                    this.stream.getTracks().forEach(track => track.stop())
                    this.stream = null
                }
                if (this.$refs.video) this.$refs.video.srcObject = null
            },
            async read() {
                const video = this.$refs.video
                if (!video || video.readyState < 2) return
                let text = null
                try {
                    if (this.detector) {
                        const codes = await this.detector.detect(video)
                        text = codes.length ? codes[0].rawValue : null
                    } else {
                        // a frame of at most 640px wide is enough for a card held to the camera
                        const scale = Math.min(1, 640 / video.videoWidth)
                        const width = Math.round(video.videoWidth * scale)
                        const height = Math.round(video.videoHeight * scale)
                        if (!this.canvas) this.canvas = document.createElement('canvas')
                        this.canvas.width = width
                        this.canvas.height = height
                        const ctx = this.canvas.getContext('2d', { willReadFrequently: true })
                        ctx.drawImage(video, 0, 0, width, height)
                        const code = jsQR(ctx.getImageData(0, 0, width, height).data, width, height, { inversionAttempts: 'dontInvert' })
                        text = code ? code.data : null
                    }
                } catch (error) {
                    text = null
                }
                if (!text) return
                const now = Date.now()
                if (text === this.last.text && now - this.last.at < REPEAT_MS) return
                this.last = { text, at: now }
                this.$emit('scan', text)
            }
        }
    }
</script>

<style scoped>
.qr-scanner__title {
    font-weight: 700;
}

.qr-scanner__view {
    position: relative;
    aspect-ratio: 4 / 3;
    margin: 0 16px;
    border-radius: 12px;
    background: #111111;
    overflow: hidden;
}

.qr-scanner__view video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* where to hold the code */
.qr-scanner__frame {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 55%;
    aspect-ratio: 1;
    border: 3px solid rgba(255, 255, 255, 0.85);
    border-radius: 16px;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.35);
    transform: translate(-50%, -50%);
    pointer-events: none;
}

.qr-scanner__error {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    color: #ffffff;
    font-weight: 600;
    text-align: center;
}

.qr-scanner__text {
    padding-top: 12px !important;
}

.qr-scanner__status {
    margin-bottom: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 700;
}

.qr-scanner__status--success {
    background: #e8f5e9;
    color: #1b5e20;
}

.qr-scanner__status--info {
    background: #eceff1;
    color: #37474f;
}

.qr-scanner__status--error {
    background: #ffebee;
    color: #b71c1c;
}

.qr-scanner__hint {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.85rem;
}
</style>
